/* Signup popup — email + SMS capture, built into the theme.
 *
 * Markup, copy and every setting live in sections/signup-popup.liquid, which
 * also drops a JSON config block this script reads. Nothing here needs editing
 * to change the offer, the question, the triggers or the Klaviyo ids.
 *
 * Flow:   offer + question  ->  email  ->  sms (optional)  ->  done
 *
 * Klaviyo is reached only through its public client API with the site's public
 * company id (no private key exists in the theme). Two calls at most per
 * signup: one email subscription (which triggers the Welcome Series that sends
 * the discount code) and one SMS subscription. A third adds gift shoppers to
 * the gift-buyer list when one is configured.
 *
 * Per-browser state is a single localStorage key: which variant they drew,
 * what they answered, when they last saw it, whether they gave an email. A
 * second key holds a random browser id. Events sent BEFORE signup (viewed,
 * answered, dismissed) go to ONE shared anonymous Klaviyo profile per store,
 * with the browser id as an event property — so views are counted for
 * visitors who never sign up without creating a profile per visitor. Events
 * after signup go to the real profile. A sessionStorage key remembers when
 * this visit landed on the site, so the delay trigger measures time on the
 * SITE rather than on each page. Every storage access is wrapped — Safari
 * private mode throws on write and a shopper with site data blocked still
 * gets a working page.
 *
 * QA on the live site without disturbing real state:
 *   ?signup_popup=test   open immediately, never read or write storage
 *   ?signup_popup=reset  clear this browser's state, then behave normally
 * In the theme editor, selecting the section opens the popup and every step can
 * be clicked through; nothing is sent to Klaviyo in design mode.
 */
(function () {
  'use strict';

  if (window.__sbsSignupPopup) return;
  window.__sbsSignupPopup = true;

  var STORAGE_KEY = 'sbs:signup-popup:v1';
  var AID_KEY = 'sbs:signup-popup:aid';
  var SHARED_ANON_ID = 'signup-popup-visitors'; /* the one anonymous profile all pre-signup events share */

  /* The shared anonymous profile is rotated DAILY ('signup-popup-visitors-2026-10-12')
     so one profile never holds more than a day of views, however big the store
     gets. Reporting counts events per METRIC, not per profile, so the rotation
     changes nothing in the numbers; the profiles stay non-billable (no email, no
     phone) and there are at most 365 of them a year. */
  function sharedAnonId() {
    return SHARED_ANON_ID + '-' + new Date().toISOString().slice(0, 10);
  }
  var LANDED_KEY = 'sbs:signup-popup:landed';
  var KLAVIYO_CLIENT = 'https://a.klaviyo.com/client';
  var COUNTRY_CODES = { US: '1', CA: '1', MX: '52', GB: '44' };
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ---------- storage ---------- */

  function loadState() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  function saveState(state) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      /* storage unavailable — the popup still works for this page view */
    }
  }

  function clearState() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(AID_KEY);
      window.sessionStorage.removeItem(LANDED_KEY);
    } catch (e) {
      /* nothing to clear */
    }
  }

  /* One random id per browser, kept for the life of the storage. Sent only as
     an event property, never as a profile identifier. */
  function anonymousIdFor() {
    var id = null;
    try {
      id = window.localStorage.getItem(AID_KEY);
    } catch (e) {
      /* unreadable storage */
    }
    if (!id) {
      try {
        id = 'sbs-' + (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12));
      } catch (e) {
        id = 'sbs-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
      }
      try {
        window.localStorage.setItem(AID_KEY, id);
      } catch (e) {
        /* not persisted — this page view still gets a stable id */
      }
    }
    return id;
  }

  /* When this visit first landed on the site (sessionStorage lives for the
     tab's session, so a second page keeps the clock running). */
  function landedAt() {
    try {
      var raw = window.sessionStorage.getItem(LANDED_KEY);
      var ts = raw ? parseInt(raw, 10) : NaN;
      if (ts > 0) return ts;
      window.sessionStorage.setItem(LANDED_KEY, String(Date.now()));
    } catch (e) {
      /* no session storage — fall back to this page's load */
    }
    return Date.now();
  }

  /* ---------- validation ---------- */

  function validEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
  }

  /* Returns E.164 or null. Accepts a pasted "+…" number as-is; otherwise applies
     the selected country's rules. NANP numbers get the area-code/exchange check
     so "1234567890" is rejected before Klaviyo has to. */
  function normalizePhone(raw, country) {
    var value = String(raw || '').trim();
    if (!value) return null;
    var digits = value.replace(/\D/g, '');
    if (value.charAt(0) === '+') {
      return digits.length >= 8 && digits.length <= 15 ? '+' + digits : null;
    }
    var cc = COUNTRY_CODES[country] || '1';
    if (cc === '1') {
      if (digits.length === 11 && digits.charAt(0) === '1') digits = digits.slice(1);
      if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(digits)) return null;
      return '+1' + digits;
    }
    if (cc === '44') {
      if (digits.charAt(0) === '0') digits = digits.slice(1);
      return digits.length >= 9 && digits.length <= 10 ? '+44' + digits : null;
    }
    if (cc === '52') {
      return digits.length === 10 ? '+52' + digits : null;
    }
    return digits.length >= 7 && digits.length <= 14 ? '+' + cc + digits : null;
  }

  function friendlyError(detail) {
    var d = String(detail || '').toLowerCase();
    if (d.indexOf('phone') !== -1) return "That number doesn't look right — try again with your area code.";
    if (d.indexOf('email') !== -1) return "That email doesn't look right — give it another try.";
    return 'Something went wrong on our end. Please try again.';
  }

  /* ---------- Klaviyo ---------- */

  function klaviyoPost(cfg, path, body) {
    var url = KLAVIYO_CLIENT + path + '?company_id=' + encodeURIComponent(cfg.klaviyo.companyId);
    return window
      .fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          revision: cfg.klaviyo.revision,
        },
        body: JSON.stringify(body),
        keepalive: true,
      })
      .then(function (res) {
        if (res.ok) return;
        return res.text().then(function (text) {
          var message = friendlyError('');
          try {
            var json = JSON.parse(text);
            if (json.errors && json.errors[0] && json.errors[0].detail) {
              message = friendlyError(json.errors[0].detail);
            }
          } catch (e) {
            /* non-JSON error body — keep the generic message */
          }
          throw new Error(message);
        });
      });
  }

  /* opts: { email, phone, channel: 'email'|'sms', listId, properties } */
  function subscribe(cfg, opts) {
    if (cfg.designMode) return Promise.resolve();
    var attributes = { properties: opts.properties || {}, subscriptions: {} };
    if (opts.email) attributes.email = opts.email;
    if (opts.phone) attributes.phone_number = opts.phone;
    if (opts.channel === 'email') attributes.subscriptions.email = { marketing: { consent: 'SUBSCRIBED' } };
    if (opts.channel === 'sms') attributes.subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: cfg.klaviyo.customSource || 'Signup Popup',
          profile: { data: { type: 'profile', attributes: attributes } },
        },
      },
    };
    if (opts.listId) {
      body.data.relationships = { list: { data: { type: 'list', id: opts.listId } } };
    }
    return klaviyoPost(cfg, '/subscriptions/', body);
  }

  /* Event straight to Klaviyo's client API — works whether or not Klaviyo's
     onsite script loaded (ad blockers eat it fairly often). Before signup the
     profile is just the anonymous id; after, email/phone ride along so the
     anonymous history merges into the real profile. */
  function trackClient(cfg, name, properties, profile) {
    if (!cfg.tracking.enabled || cfg.designMode) return;
    if (!profile || (!profile.email && !profile.phone_number && !profile.anonymous_id)) return;
    /* unique_id is REQUIRED for correct counts: without it Klaviyo keeps only one
       event per profile per metric per second, and every pre-signup event in the
       store shares one profile — two visitors in the same second would be one view. */
    var uniqueId = ((properties && properties.browser_id) || 'nobrowser') + ':' + name + ':' + Date.now() + ':' + Math.random().toString(36).slice(2, 10);
    var body = {
      data: {
        type: 'event',
        attributes: {
          metric: { data: { type: 'metric', attributes: { name: cfg.tracking.prefix + ' ' + name } } },
          properties: properties || {},
          unique_id: uniqueId,
          profile: { data: { type: 'profile', attributes: profile } },
        },
      },
    };
    klaviyoPost(cfg, '/events/', body).catch(function () {
      /* analytics must never break the flow */
    });
  }

  function identifyOnsite(email) {
    try {
      var k = window.klaviyo;
      if (k && typeof k.push === 'function') k.push(['identify', { email: email }]);
    } catch (e) {
      /* ignore */
    }
  }

  /* Resolves true only when Klaviyo's onsite script is present AND already
     knows this browser. Bounded: a blocked or slow script must not stall the
     popup, so an unanswered check counts as "not identified". */
  function klaviyoIdentified(timeoutMs) {
    return new Promise(function (resolve) {
      var settled = false;
      function settle(value) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(!!value);
      }
      var timer = setTimeout(function () {
        settle(false);
      }, timeoutMs);
      try {
        var k = window.klaviyo;
        if (k && typeof k.isIdentified === 'function') {
          Promise.resolve(k.isIdentified()).then(settle, function () {
            settle(false);
          });
          return;
        }
      } catch (e) {
        /* fall through */
      }
      settle(false);
    });
  }

  /* Crawlers and automation get no popup and send no events. Googlebot renders
     pages with a fast-forwarded clock, so the delay timer alone is not proof of
     a person; navigator.webdriver and the UA cover the common cases. */
  function looksLikeBot() {
    try {
      if (navigator.webdriver) return true;
      var ua = navigator.userAgent || '';
      return /bot|crawl|spider|slurp|headless|lighthouse|pagespeed|gtmetrix|pingdom|ptst|facebookexternalhit|bingpreview|screenshot|prerender/i.test(ua);
    } catch (e) {
      return false;
    }
  }

  /* A view is only counted once the visitor has done something a person does:
     moved a pointer, scrolled, touched or typed. Bots that execute scripts
     rarely do, and a tab that was never looked at never does. */
  var humanSeen = false;
  var humanWaiters = [];
  function onHuman() {
    if (humanSeen) return;
    humanSeen = true;
    humanWaiters.splice(0).forEach(function (fn) {
      fn();
    });
  }
  ['pointerdown', 'pointermove', 'touchstart', 'keydown', 'wheel', 'scroll'].forEach(function (evt) {
    try {
      window.addEventListener(evt, onHuman, { passive: true, once: true, capture: true });
    } catch (e) {
      window.addEventListener(evt, onHuman, true);
    }
  });
  function whenHuman(fn) {
    if (humanSeen) fn();
    else humanWaiters.push(fn);
  }

  /* Shopify applies a discount to the session when /discount/<code> is visited.
     Redirecting to /cart.js keeps the follow-up response tiny. */
  function applyDiscount(cfg) {
    var code = cfg.reward && cfg.reward.code;
    if (!code || !cfg.reward.autoApply || cfg.designMode) return;
    try {
      window
        .fetch('/discount/' + encodeURIComponent(code) + '?redirect=%2Fcart.js', { credentials: 'same-origin' })
        .catch(function () {});
    } catch (e) {
      /* ignore */
    }
  }

  /* ---------- one popup instance ---------- */

  function setup(root) {
    if (!root || root.__sbsReady) return;
    var sid = root.getAttribute('data-signup-popup');
    var cfgEl = document.getElementById('SignupPopupConfig-' + sid);
    if (!cfgEl) return;
    var cfg;
    try {
      cfg = JSON.parse(cfgEl.textContent);
    } catch (e) {
      return;
    }
    root.__sbsReady = true;

    var params = new URLSearchParams(window.location.search);
    var qaMode = params.get('signup_popup');
    if (qaMode === 'reset') clearState();
    var persist = !cfg.designMode && qaMode !== 'test';
    var state = persist ? loadState() : {};
    var save = function () {
      if (persist) saveState(state);
    };
    var anonymousId = anonymousIdFor();

    var dialog = root.querySelector('.sbs-popup__dialog');
    var teaser = document.querySelector('[data-sbs-teaser="' + sid + '"]');
    var stepEls = {};
    Array.prototype.forEach.call(root.querySelectorAll('[data-sbs-step]'), function (el) {
      stepEls[el.getAttribute('data-sbs-step')] = el;
    });
    var dots = root.querySelectorAll('[data-sbs-dot]');
    var ORDER = ['offer', 'email', 'sms', 'done'];
    var current = 'offer';
    var openedAt = 0;
    var lastFocus = null;
    var isOpen = false;
    var armed = false;

    /* ----- A/B variant: drawn once per browser, saved on the profile ----- */
    var ab = cfg.abTest || {};
    var abEnabled = !!ab.enabled;
    var forcedVariant = params.get('signup_popup_variant'); /* QA: ?signup_popup_variant=B shows variant B */
    if (forcedVariant !== 'A' && forcedVariant !== 'B') forcedVariant = null;
    if (!forcedVariant && abEnabled && !state.variant) {
      state.variant = Math.random() * 100 < (ab.splitPercent || 50) ? 'B' : 'A';
      save();
    }
    /* With no test running, the version label stands in for the variant, so the
       analytics split before/after a copy change the same way they split A/B. */
    var variant = forcedVariant || (abEnabled ? state.variant : (cfg.version || 'A'));
    if (variant === 'B') {
      /* Variant B swaps the copy only; the question, lists, discount and
         analytics are identical, so the test isolates the framing. */
      var setCopy = function (selector, text) {
        var el = root.querySelector(selector);
        if (el && text) el.textContent = text;
      };
      setCopy('[data-sbs-headline]', ab.headline);
      setCopy('[data-sbs-offer]', ab.offer);
      setCopy('[data-sbs-subheadline]', ab.subheadline);
      setCopy('[data-sbs-email-sub]', ab.emailIntro);
      setCopy('[data-sbs-email-label]', ab.emailButton);
      setCopy('[data-sbs-sms-head]', ab.smsHeadline);
      setCopy('[data-sbs-sms-sub]', ab.smsSubheadline);
      setCopy('[data-sbs-sms-label]', ab.smsButton);
      setCopy('[data-sbs-done-sub]', ab.doneSubheadline);
      setCopy('[data-sbs-done-cta]', ab.doneButton);
      if (ab.emailSubGift) cfg.copy.emailSubGift = ab.emailSubGift;
    }

    /* phone / tablet / desktop, for the device split in the analytics */
    function deviceClass() {
      try {
        if (window.matchMedia('(max-width: 749px)').matches) return 'phone';
        if (window.matchMedia('(pointer: coarse)').matches) return 'tablet';
      } catch (e) {
        /* ignore */
      }
      return 'desktop';
    }

    function baseProps() {
      return {
        variant: variant,
        shopping_for: state.answerText || null,
        page: window.location.pathname,
        browser_id: anonymousId,
        device: deviceClass(),
      };
    }

    function profileProps() {
      var props = { signup_popup_variant: variant, signup_popup_page: window.location.pathname, signup_popup_browser_id: anonymousId };
      if (cfg.question.property && state.answerText) props[cfg.question.property] = state.answerText;
      return props;
    }

    /* ----- steps ----- */

    var hero = root.querySelector('[data-sbs-hero]');

    function goTo(name) {
      current = name;
      root.setAttribute('data-sbs-current', name);
      ORDER.forEach(function (step) {
        if (stepEls[step]) stepEls[step].hidden = step !== name;
      });
      if (hero) hero.hidden = name === 'done';
      var idx = ORDER.indexOf(name);
      Array.prototype.forEach.call(dots, function (dot) {
        var dotIdx = ORDER.indexOf(dot.getAttribute('data-sbs-dot'));
        dot.classList.toggle('is-active', dotIdx === idx);
        dot.classList.toggle('is-done', dotIdx < idx);
      });
      if (dialog) dialog.scrollTop = 0;
      /* Desktop: land in the field. Touch: focus the heading instead, so the
         keyboard doesn't jump up before they've read the step. */
      var finePointer = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
      var focusTarget = stepEls[name] && ((finePointer && stepEls[name].querySelector('input:not([tabindex="-1"])')) || stepEls[name].querySelector('.sbs-popup__title') || stepEls[name]);
      if (focusTarget) {
        if (focusTarget.tagName !== 'INPUT') focusTarget.setAttribute('tabindex', '-1');
        try {
          focusTarget.focus({ preventScroll: true });
        } catch (e) {
          /* ignore */
        }
      }
    }

    function showError(form, message) {
      var el = form.querySelector('[data-sbs-error]');
      if (!el) return;
      el.textContent = message || '';
      el.hidden = !message;
    }

    function setBusy(form, busy) {
      var btn = form.querySelector('[data-sbs-submit]');
      if (!btn) return;
      btn.disabled = !!busy;
      btn.classList.toggle('is-busy', !!busy);
      var label = btn.querySelector('[data-sbs-btn-label]');
      if (label) {
        if (busy) {
          label.setAttribute('data-sbs-label', label.textContent);
          label.textContent = 'One moment…';
        } else if (label.getAttribute('data-sbs-label')) {
          label.textContent = label.getAttribute('data-sbs-label');
        }
      }
    }

    /* ----- open / close ----- */

    function updateTeaser() {
      if (!teaser) return;
      var subscribed = !!(state.emailAt || state.completedAt || cfg.knownSubscriber || state.knownAt);
      var onExcluded = /^\/(cart|checkout|checkouts)(\/|$)/.test(window.location.pathname);
      var show = !isOpen && !subscribed && !onExcluded && (state.dismissedAt || state.lastShownAt ? true : !!cfg.teaser.beforeOpen);
      if (cfg.designMode) show = false;
      teaser.hidden = !show;
    }

    function open(reason) {
      if (isOpen) return;
      isOpen = true;
      openedAt = Date.now();
      lastFocus = document.activeElement;
      root.hidden = false;
      document.documentElement.classList.add('sbs-popup-open');
      goTo(state.emailAt && !state.completedAt ? 'sms' : 'offer');
      if (state.answer !== undefined) {
        Array.prototype.forEach.call(root.querySelectorAll('[data-sbs-answer]'), function (btn) {
          btn.classList.toggle('is-selected', String(state.answer) === btn.getAttribute('data-sbs-answer'));
        });
      }
      state.shows = (state.shows || 0) + 1;
      state.lastShownAt = Date.now();
      save();
      updateTeaser();
      /* One view per browser per cooldown window, so reopening from the
         floating button does not inflate the denominator. */
      var cooldownMs = (cfg.triggers.cooldownHours || 24) * 3600e3;
      if (!state.lastViewedAt || Date.now() - state.lastViewedAt >= cooldownMs) {
        state.lastViewedAt = Date.now();
        save();
        var viewProps = Object.assign(baseProps(), { reason: reason });
        whenHuman(function () {
          trackClient(cfg, 'Viewed', viewProps, identity());
        });
      }
    }

    function close(reason) {
      if (!isOpen) return;
      isOpen = false;
      root.hidden = true;
      document.documentElement.classList.remove('sbs-popup-open');
      if (lastFocus && typeof lastFocus.focus === 'function') {
        try {
          lastFocus.focus({ preventScroll: true });
        } catch (e) {
          /* ignore */
        }
      }
      if (!cfg.designMode) {
        if (current === 'done' || state.emailAt) {
          /* email already captured: the welcome email is on its way, we're done */
          state.completedAt = state.completedAt || Date.now();
        } else {
          state.dismissedAt = Date.now();
          trackClient(cfg, 'Dismissed', Object.assign(baseProps(), { step: current, reason: reason }), identity());
        }
        save();
      }
      updateTeaser();
    }

    function finish(smsAdded) {
      goTo('done');
      state.completedAt = Date.now();
      save();
      trackClient(cfg, 'Completed', Object.assign(baseProps(), { sms_added: !!smsAdded }), identity());
      updateTeaser();
    }

    /* Who an event belongs to: the real profile once we have an email, else
       the store's single shared anonymous profile. */
    function identity() {
      if (!state.email && !state.phone) return { anonymous_id: sharedAnonId() };
      var p = {};
      if (state.email) p.email = state.email;
      if (state.phone) p.phone_number = state.phone;
      return p;
    }

    /* ----- eligibility + triggers ----- */

    function excludedPath() {
      var path = window.location.pathname;
      if (/^\/(cart|checkout|checkouts)(\/|$)/.test(path)) return true;
      if (cfg.triggers.hideOnProduct && cfg.templateName === 'product') return true;
      var list = String(cfg.triggers.excludePaths || '')
        .split(/[\n,]/)
        .map(function (s) {
          return s.trim().replace(/\/+$/, '');
        })
        .filter(Boolean);
      return list.some(function (prefix) {
        return path === prefix || path.indexOf(prefix + '/') === 0;
      });
    }

    function autoEligible() {
      if (cfg.designMode) return false;
      if (qaMode === 'test') return true;
      if (state.completedAt || state.emailAt) return false;
      if (cfg.knownSubscriber || state.knownAt) return false;
      if (excludedPath()) return false;
      /* Closed it (X, Escape, backdrop): the Klaviyo form's rule — not again for
         `dismissDays` (default 5). Saw it and just left: not again for
         `cooldownHours` (default 24 h). Subscribed: never (checked above). */
      var dismissMs = (cfg.triggers.dismissDays || 5) * 86400e3;
      if (state.dismissedAt && Date.now() - state.dismissedAt < dismissMs) return false;
      var cooldown = (cfg.triggers.cooldownHours || 24) * 3600e3;
      if (state.lastShownAt && Date.now() - state.lastShownAt < cooldown) return false;
      return true;
    }

    var fired = false;
    function tryOpen(reason) {
      if (fired || isOpen) return;
      if (!autoEligible()) return;
      fired = true;
      var check = cfg.triggers.hideKnown && qaMode !== 'test' ? klaviyoIdentified(1500) : Promise.resolve(false);
      check.then(function (known) {
        if (known) {
          state.knownAt = Date.now();
          save();
          updateTeaser();
          return;
        }
        /* A background tab is not a view: wait until it is actually looked at. */
        if (document.hidden) {
          document.addEventListener('visibilitychange', function onVisible() {
            if (document.hidden) return;
            document.removeEventListener('visibilitychange', onVisible);
            open(reason);
          });
          return;
        }
        open(reason);
      });
    }

    function armTriggers() {
      if (armed || cfg.designMode) return;
      armed = true;
      if (looksLikeBot() && qaMode !== 'test') return;
      if (qaMode === 'test') {
        tryOpen('test');
        return;
      }
      if (!autoEligible()) return;
      var t = cfg.triggers;
      var startedAt = Date.now();
      if (t.delaySeconds > 0) {
        /* Time on the SITE, not on this page: a visitor who clicked through
           to a second page keeps the seconds already spent. */
        var elapsed = Date.now() - landedAt();
        setTimeout(function () {
          tryOpen('delay');
        }, Math.max(250, t.delaySeconds * 1000 - elapsed));
      }
      if (t.scrollPercent > 0) {
        var onScroll = function () {
          var doc = document.documentElement;
          var max = doc.scrollHeight - window.innerHeight;
          if (max <= 0) return;
          if ((window.scrollY / max) * 100 >= t.scrollPercent) {
            window.removeEventListener('scroll', onScroll);
            tryOpen('scroll');
          }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
      }
      if (t.exitIntent && window.matchMedia && window.matchMedia('(pointer: fine)').matches) {
        document.addEventListener('mouseleave', function (e) {
          if (e.clientY <= 0 && Date.now() - startedAt > 3000) tryOpen('exit');
        });
      }
    }

    /* ----- wiring ----- */

    Array.prototype.forEach.call(root.querySelectorAll('[data-sbs-close]'), function (el) {
      el.addEventListener('click', function () {
        close('close');
      });
    });

    var decline = root.querySelector('[data-sbs-decline]');
    if (decline) {
      decline.addEventListener('click', function () {
        close('declined');
      });
    }

    Array.prototype.forEach.call(root.querySelectorAll('[data-sbs-answer]'), function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(btn.getAttribute('data-sbs-answer'), 10);
        state.answer = idx;
        state.answerText = cfg.question.answers[idx] || '';
        save();
        Array.prototype.forEach.call(root.querySelectorAll('[data-sbs-answer]'), function (b) {
          b.classList.toggle('is-selected', b === btn);
        });
        var emailSub = root.querySelector('[data-sbs-email-sub]');
        if (emailSub) {
          var gift = idx === cfg.question.giftAnswerIndex && cfg.copy.emailSubGift;
          emailSub.textContent = gift ? cfg.copy.emailSubGift : emailSub.__sbsDefault;
        }
        trackClient(cfg, 'Answered', baseProps(), identity());
        goTo('email');
      });
    });
    var emailSubEl = root.querySelector('[data-sbs-email-sub]');
    if (emailSubEl) emailSubEl.__sbsDefault = emailSubEl.textContent;

    var emailForm = root.querySelector('[data-sbs-form="email"]');
    if (emailForm) {
      emailForm.addEventListener('submit', function (e) {
        e.preventDefault();
        if (emailForm.__busy) return;
        var input = emailForm.querySelector('input[name="email"]');
        var hp = emailForm.querySelector('input[name="website"]');
        var email = (input.value || '').trim().toLowerCase();
        showError(emailForm, '');
        input.removeAttribute('aria-invalid');
        if (!validEmail(email)) {
          input.setAttribute('aria-invalid', 'true');
          showError(emailForm, 'Please enter a valid email address.');
          input.focus();
          return;
        }
        /* Honeypot filled, or submitted faster than a human could type: act
           satisfied, send nothing. */
        if ((hp && hp.value) || Date.now() - openedAt < 1200) {
          goTo('sms');
          return;
        }
        emailForm.__busy = true;
        setBusy(emailForm, true);
        subscribe(cfg, { email: email, channel: 'email', listId: cfg.klaviyo.listEmail, properties: profileProps() })
          .then(function () {
            state.email = email;
            state.emailAt = Date.now();
            save();
            identifyOnsite(email);
            trackClient(cfg, 'Email Submitted', baseProps(), identity());
            if (state.answer === cfg.question.giftAnswerIndex && cfg.klaviyo.listGift) {
              subscribe(cfg, { email: email, channel: 'email', listId: cfg.klaviyo.listGift, properties: profileProps() }).catch(function () {});
            }
            applyDiscount(cfg);
            goTo('sms');
          })
          .catch(function (err) {
            showError(emailForm, (err && err.message) || friendlyError(''));
          })
          .then(function () {
            emailForm.__busy = false;
            setBusy(emailForm, false);
          });
      });
    }

    var smsForm = root.querySelector('[data-sbs-form="sms"]');
    if (smsForm) {
      var countrySel = smsForm.querySelector('select[name="country"]');
      if (countrySel && cfg.defaultCountry) countrySel.value = cfg.defaultCountry;
      smsForm.addEventListener('submit', function (e) {
        e.preventDefault();
        if (smsForm.__busy) return;
        var input = smsForm.querySelector('input[name="phone"]');
        var phone = normalizePhone(input.value, countrySel ? countrySel.value : cfg.defaultCountry);
        showError(smsForm, '');
        input.removeAttribute('aria-invalid');
        if (!phone) {
          input.setAttribute('aria-invalid', 'true');
          showError(smsForm, 'Please enter a valid phone number, area code included.');
          input.focus();
          return;
        }
        smsForm.__busy = true;
        setBusy(smsForm, true);
        subscribe(cfg, { email: state.email, phone: phone, channel: 'sms', listId: cfg.klaviyo.listSms, properties: profileProps() })
          .then(function () {
            state.phone = phone;
            state.smsAt = Date.now();
            save();
            trackClient(cfg, 'SMS Submitted', baseProps(), identity());
            finish(true);
          })
          .catch(function (err) {
            showError(smsForm, (err && err.message) || friendlyError(''));
          })
          .then(function () {
            smsForm.__busy = false;
            setBusy(smsForm, false);
          });
      });
      var skip = smsForm.querySelector('[data-sbs-skip]');
      if (skip) {
        skip.addEventListener('click', function () {
          trackClient(cfg, 'SMS Skipped', baseProps(), identity());
          finish(false);
        });
      }
    }

    var doneCta = root.querySelector('[data-sbs-done-cta]');
    if (doneCta) {
      doneCta.addEventListener('click', function (e) {
        state.completedAt = state.completedAt || Date.now();
        save();
        if (doneCta.getAttribute('href') === '#') {
          e.preventDefault();
          close('done');
        }
      });
    }

    if (teaser) {
      teaser.addEventListener('click', function () {
        open('teaser');
      });
    }

    /* Keyboard: Esc closes, Tab stays inside the dialog. */
    root.addEventListener('keydown', function (e) {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        close('escape');
        return;
      }
      if (e.key !== 'Tab' || !dialog) return;
      var items = Array.prototype.filter.call(dialog.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null;
      });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    /* Theme editor: selecting the section opens it; the steps are clickable. */
    if (cfg.designMode) {
      document.addEventListener('shopify:section:select', function (e) {
        if (e.detail && e.detail.sectionId === sid) open('editor');
      });
      document.addEventListener('shopify:section:deselect', function (e) {
        if (e.detail && e.detail.sectionId === sid) close('editor');
      });
    }

    /* Warm the background image for this viewport so the takeover never
       flashes white while the graphic arrives. */
    function preloadBackground() {
      try {
        var isMobile = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
        var names = isMobile ? ['--sbs-bg-mobile', '--sbs-scene-mobile'] : ['--sbs-bg-desktop', '--sbs-scene-desktop'];
        names.forEach(function (name) {
          var raw = getComputedStyle(root).getPropertyValue(name);
          var m = /url\((['"]?)(.*?)\1\)/.exec(raw || '');
          if (m && m[2]) {
            var img = new Image();
            img.src = m[2];
          }
        });
      } catch (e) {
        /* ignore */
      }
    }

    preloadBackground();
    updateTeaser();
    armTriggers();
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-signup-popup]'), setup);
  }

  /* The editor re-renders the section on every settings change. */
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target && e.target.querySelector && e.target.querySelector('[data-signup-popup]');
    if (root) {
      setup(root);
      var evt;
      try {
        evt = new CustomEvent('shopify:section:select', { detail: { sectionId: root.getAttribute('data-signup-popup') } });
        document.dispatchEvent(evt);
      } catch (err) {
        /* ignore */
      }
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
